import type { StationKind, VehicleType } from '@/state/types';

export type VehicleCategory = 'fire' | 'ems' | 'police';

/**
 * Block B — vehicle specs taken 1:1 from the Wiki. No own balancing.
 * Mindestbesatzungen aus dem Wiki werden bewusst ignoriert (kein Personalsystem).
 */
export interface VehicleDefaultModule {
  family: string;
  size?: string;
}

export interface VehicleSpec {
  type: VehicleType;
  label: string;
  fullName: string;
  category: VehicleCategory;
  /** Purchase price in €. */
  cost: number;
  /** Daily upkeep in € (real-day). */
  upkeepPerDay: number;
  /** Travel-speed scaling vs OSRM baseline. */
  speedMultiplier: number;
  /** Number of equippable modules (Phase 6 mechanic). */
  moduleSlots: number;
  /** Water tank capacity in liters (0 if none). */
  waterTankMax: number;
  /** Breathing-air supply capacity in liters (only GW carries supply). */
  breathingAirMax: number;
  /** Which station kinds can host this vehicle. */
  allowedStations: StationKind[];
  /** Short blurb shown in the purchase dialog. */
  description: string;
  /**
   * Phase 9.3 — Module die "ab Werk" mitkommen. Im Grundpreis enthalten,
   * füllen Slots beim Kauf, sind aber nachträglich tauschbar (mit reduzierter
   * Erstattung von 30 % statt 50 %).
   */
  defaultModules?: VehicleDefaultModule[];
  /** Phase 9.6.5 — Mindest-Tier-Level der hostenden Wache (z. B. FWK braucht BF Tier 4). */
  minStationTier?: number;
  /**
   * Phase 10.2 — Air unit (helicopter). Routing collapses to a straight-line
   * leg (no OSRM detour) because the chopper isn't bound to streets.
   */
  airUnit?: boolean;
  /**
   * Phase 10.4.4 — Vehicle drives with no Sondersignal (no siren burst, no
   * blue-light marker effect). Currently used by MEK only.
   */
  covertOperation?: boolean;
}

const FIRE: StationKind[] = ['bf-station', 'ff-station'];
const FIRE_BF_ONLY: StationKind[] = ['bf-station'];
const EMS: StationKind[] = ['ems-station'];
const POLICE_REVIER: StationKind[] = ['police-station', 'federal-police-station'];
const POLICE_REVIER_OR_RIOT: StationKind[] = [
  'police-station',
  'riot-police-station',
  'federal-police-station',
];
const POLICE_RIOT: StationKind[] = ['riot-police-station'];
const POLICE_SEK: StationKind[] = ['sek-base'];
const POLICE_FEDERAL: StationKind[] = ['federal-police-station'];

export const VEHICLE_SPECS: Record<VehicleType, VehicleSpec> = {
  /* ─────────── Feuerwehr ─────────── */
  ELW: {
    type: 'ELW',
    label: 'ELW',
    fullName: 'Einsatzleitwagen',
    category: 'fire',
    cost: 200_000,
    upkeepPerDay: 40,
    speedMultiplier: 1.4,
    moduleSlots: 2,
    waterTankMax: 0,
    breathingAirMax: 0,
    // Phase 13.4 — FF darf jetzt ELW unterstellen.
    allowedStations: FIRE,
    description: 'Führungs- und Kommunikationszentrale.',
  },
  MTW: {
    type: 'MTW',
    label: 'MTW',
    fullName: 'Mannschaftstransportwagen',
    category: 'fire',
    cost: 100_000,
    upkeepPerDay: 30,
    speedMultiplier: 1.2,
    moduleSlots: 1,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Mannschafts- und Materialtransport.',
  },
  DLK: {
    type: 'DLK',
    label: 'DLK',
    fullName: 'Drehleiter mit Korb',
    category: 'fire',
    cost: 850_000,
    upkeepPerDay: 115,
    speedMultiplier: 1.0,
    moduleSlots: 4,
    waterTankMax: 0,
    breathingAirMax: 0,
    // Phase 13.4 — DLK auch in FF erlaubt.
    allowedStations: FIRE,
    description: 'Höhenrettung und Wasserwerfer-Korb.',
    defaultModules: [{ family: 'dlk-ladder' }],
  },
  PKW: {
    type: 'PKW',
    label: 'PKW',
    fullName: 'Kommandowagen',
    category: 'fire',
    cost: 50_000,
    upkeepPerDay: 20,
    speedMultiplier: 1.4,
    moduleSlots: 2,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Schneller Kommandowagen für Führungskräfte.',
  },
  TLF: {
    type: 'TLF',
    label: 'TLF',
    fullName: 'Tanklöschfahrzeug',
    category: 'fire',
    cost: 420_000,
    upkeepPerDay: 90,
    speedMultiplier: 1.0,
    moduleSlots: 4,
    waterTankMax: 5_000,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Großer Wassertank für Vegetationsbrände.',
    defaultModules: [{ family: 'water-tank', size: '2000l' }],
  },
  HLF: {
    type: 'HLF',
    label: 'HLF',
    fullName: 'Hilfeleistungslöschfahrzeug',
    category: 'fire',
    cost: 600_000,
    upkeepPerDay: 100,
    speedMultiplier: 1.0,
    moduleSlots: 8,
    waterTankMax: 2_000,
    breathingAirMax: 0,
    // Phase 13.4 — HLF auch in FF erlaubt.
    allowedStations: FIRE,
    description: 'Allrounder: Löschen + technische Hilfe.',
    defaultModules: [
      { family: 'water-tank', size: '1000l' },
      { family: 'breathing-air', size: 'small' },
      { family: 'hydraulic-rescue-set', size: 'large' },
      { family: 'winch' },
      { family: 'technical-equipment' },
    ],
  },
  LF: {
    type: 'LF',
    label: 'LF',
    fullName: 'Löschgruppenfahrzeug',
    category: 'fire',
    cost: 360_000,
    upkeepPerDay: 80,
    speedMultiplier: 1.0,
    moduleSlots: 5,
    waterTankMax: 1_600,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Standard-Löschfahrzeug.',
    defaultModules: [
      { family: 'water-tank', size: '1200l' },
      { family: 'breathing-air', size: 'small' },
      { family: 'hydraulic-rescue-set', size: 'small' },
    ],
  },
  GW: {
    type: 'GW',
    label: 'GW',
    fullName: 'Gerätewagen Atemschutz',
    category: 'fire',
    cost: 200_000,
    upkeepPerDay: 50,
    speedMultiplier: 1.0,
    // Phase 13.5 — Standard-GW jetzt mit 6 Slots (vorher 2). Realistisch hat
    // ein GW je nach Variante 4–8 Slotplätze.
    moduleSlots: 6,
    waterTankMax: 0,
    breathingAirMax: 80_000,
    allowedStations: FIRE,
    description: 'Atemluft-Versorgungseinheit für Großeinsätze.',
  },
  // Phase 13.5 — Neue GW-Varianten
  GWL: {
    type: 'GWL',
    label: 'GW-L',
    fullName: 'Gerätewagen Logistik',
    category: 'fire',
    cost: 250_000,
    upkeepPerDay: 60,
    speedMultiplier: 1.0,
    moduleSlots: 8,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Schwere Ausrüstung, Großzelt, Verpflegung. Großvolumig.',
  },
  GWG: {
    type: 'GWG',
    label: 'GW-G',
    fullName: 'Gerätewagen Gefahrgut',
    category: 'fire',
    cost: 280_000,
    upkeepPerDay: 70,
    speedMultiplier: 1.0,
    moduleSlots: 5,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'ABC-Schutz, Spezialmodule für Gefahrgutlagen.',
  },
  RW: {
    type: 'RW',
    label: 'RW',
    fullName: 'Rüstwagen',
    category: 'fire',
    cost: 500_000,
    upkeepPerDay: 80,
    speedMultiplier: 1.0,
    moduleSlots: 5,
    waterTankMax: 0,
    breathingAirMax: 0,
    // Phase 13.4 — RW auch in FF erlaubt.
    allowedStations: FIRE,
    description: 'Schwere technische Hilfe (Spreizer, Schere).',
  },
  SW: {
    type: 'SW',
    label: 'SW',
    fullName: 'Schlauchwagen',
    category: 'fire',
    cost: 180_000,
    upkeepPerDay: 40,
    speedMultiplier: 1.0,
    moduleSlots: 2,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Verlegt B-Schläuche zur Wasserversorgung.',
  },
  TSF: {
    type: 'TSF',
    label: 'TSF',
    fullName: 'Tragkraftspritzenfahrzeug',
    category: 'fire',
    cost: 250_000,
    upkeepPerDay: 65,
    speedMultiplier: 1.2,
    moduleSlots: 5,
    waterTankMax: 600,
    breathingAirMax: 0,
    allowedStations: FIRE,
    description: 'Kleinster Löschangriff, ideal für FF-Gerätehäuser.',
  },
  /* Phase 9.6.5 — Feuerwehrkran */
  FWK: {
    type: 'FWK',
    label: 'FWK',
    fullName: 'Feuerwehrkran',
    category: 'fire',
    cost: 1_200_000,
    upkeepPerDay: 130,
    speedMultiplier: 0.8,
    moduleSlots: 3,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: FIRE_BF_ONLY,
    description: 'Schwerlast-Hebearbeiten — exklusiv BF Tier ≥ 4.',
    defaultModules: [{ family: 'crane' }],
    minStationTier: 4,
  },

  /* ─────────── Rettungsdienst ─────────── */
  KTW: {
    type: 'KTW',
    label: 'KTW',
    fullName: 'Krankentransportwagen',
    category: 'ems',
    cost: 130_000,
    upkeepPerDay: 70,
    speedMultiplier: 1.1,
    moduleSlots: 1,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: EMS,
    description: 'Nicht-akuter Patiententransport.',
  },
  RTW: {
    type: 'RTW',
    label: 'RTW',
    fullName: 'Rettungswagen',
    category: 'ems',
    cost: 220_000,
    upkeepPerDay: 70,
    speedMultiplier: 1.1,
    moduleSlots: 1,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: EMS,
    description: 'Standardtransport mit Notfallversorgung.',
  },
  NEF: {
    type: 'NEF',
    label: 'NEF',
    fullName: 'Notarzteinsatzfahrzeug',
    category: 'ems',
    cost: 120_000,
    upkeepPerDay: 50,
    speedMultiplier: 1.4,
    moduleSlots: 0,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: EMS,
    description: 'Bringt Notarzt zum Einsatzort.',
  },

  /* ─────────── Polizei (Phase 10.2) ─────────── */
  FuStW: {
    type: 'FuStW',
    label: 'FuStW',
    fullName: 'Funkstreifenwagen',
    category: 'police',
    cost: 80_000,
    upkeepPerDay: 25,
    speedMultiplier: 1.4,
    moduleSlots: 2,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_REVIER,
    description: 'Standard-Streifenwagen.',
    defaultModules: [{ family: 'arrest-equipment', size: 'standard' }],
  },
  GruKW: {
    type: 'GruKW',
    label: 'GruKW',
    fullName: 'Gruppenkraftwagen',
    category: 'police',
    cost: 140_000,
    upkeepPerDay: 40,
    speedMultiplier: 1.2,
    moduleSlots: 3,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_REVIER_OR_RIOT,
    description: 'Mannschaftstransport für 8 Beamte.',
    defaultModules: [{ family: 'arrest-equipment', size: 'extended' }],
  },
  KradPol: {
    type: 'KradPol',
    label: 'Krad',
    fullName: 'Polizei-Motorrad',
    category: 'police',
    cost: 35_000,
    upkeepPerDay: 15,
    speedMultiplier: 1.7,
    moduleSlots: 1,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_REVIER,
    description: 'Schnelle Anfahrt, keine Festnahme-Kapazität.',
  },
  // Phase 13.3 — Ziviler Kommando-Streifenwagen
  PolPKW: {
    type: 'PolPKW',
    label: 'PKW',
    fullName: 'Polizei-Kommandowagen',
    category: 'police',
    cost: 60_000,
    upkeepPerDay: 20,
    speedMultiplier: 1.4,
    moduleSlots: 1,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_REVIER,
    description: 'Ziviler Kommandowagen für Verkehrskontrollen.',
  },
  // Phase 13.3 — Gefangenentransporter
  GefKW: {
    type: 'GefKW',
    label: 'GefKW',
    fullName: 'Gefangenenkraftwagen',
    category: 'police',
    cost: 180_000,
    upkeepPerDay: 50,
    speedMultiplier: 1.1,
    moduleSlots: 4,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_REVIER,
    description: 'Spezialfahrzeug für Gefangenen-Überstellungen (8 Plätze).',
    defaultModules: [{ family: 'arrest-equipment', size: 'extended' }],
  },
  WaWe: {
    type: 'WaWe',
    label: 'WaWe',
    fullName: 'Wasserwerfer',
    category: 'police',
    cost: 1_400_000,
    upkeepPerDay: 200,
    speedMultiplier: 0.7,
    moduleSlots: 4,
    waterTankMax: 10_000,
    breathingAirMax: 0,
    allowedStations: POLICE_RIOT,
    description: 'Wasserwerfer 10000 für Crowd Control.',
    defaultModules: [{ family: 'water-cannon', size: '10000l' }],
  },
  SEKWg: {
    type: 'SEKWg',
    label: 'SEK-Wg',
    fullName: 'SEK-Wagen',
    category: 'police',
    cost: 800_000,
    upkeepPerDay: 150,
    speedMultiplier: 1.0,
    moduleSlots: 5,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_SEK,
    description: 'Gepanzerter SEK-Transporter.',
    defaultModules: [
      { family: 'tactical-gear', size: 'standard' },
      { family: 'breaching-equipment' },
    ],
  },
  MEKWg: {
    type: 'MEKWg',
    label: 'MEK-Wg',
    fullName: 'Mobiles Einsatzkommando',
    category: 'police',
    cost: 600_000,
    upkeepPerDay: 130,
    speedMultiplier: 1.3,
    moduleSlots: 4,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_SEK,
    description: 'Verdeckter Einsatz, ziviler Auftritt — kein Sondersignal.',
    defaultModules: [
      { family: 'tactical-gear', size: 'standard' },
      { family: 'surveillance-tech' },
    ],
    covertOperation: true,
  },
  PolHelo: {
    type: 'PolHelo',
    label: 'PolHelo',
    fullName: 'Polizeihubschrauber',
    category: 'police',
    cost: 4_500_000,
    upkeepPerDay: 800,
    speedMultiplier: 2.5,
    moduleSlots: 3,
    waterTankMax: 0,
    breathingAirMax: 0,
    allowedStations: POLICE_FEDERAL,
    description: 'Polizeihubschrauber für Großlagen — Luftlinien-Routing.',
    airUnit: true,
  },
};

export const VEHICLE_SPEC_LIST: VehicleSpec[] = Object.values(VEHICLE_SPECS);

/** Vehicle options available at a given station kind, ordered by cost. */
export function vehiclesForStation(kind: StationKind): VehicleSpec[] {
  return VEHICLE_SPEC_LIST.filter((v) => v.allowedStations.includes(kind)).sort(
    (a, b) => a.cost - b.cost,
  );
}
